/**
 * File card component for displaying file information
 */

import { forwardRef } from 'react';
import { Card, Stack, Text, Group, Button, Badge, Avatar } from '@/components/ui';
import {
  IconDownload,
  IconTrash,
  IconEye,
} from '@tabler/icons-react';
import { formatFileSize, formatTimeAgo } from '../../utils/formatting';
import { getFileIcon, getFileMimeColor, getFileTypeName } from '../utils';
import type { FileCardProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * File card component for displaying individual file information
 *
 * @example
 * ```tsx
 * <FileCard
 *   file={{
 *     id: 'file-123',
 *     fileName: 'document.pdf',
 *     mimeType: 'application/pdf',
 *     fileSize: 1024000,
 *     uploadedAt: '2024-01-15T10:30:00Z',
 *   }}
 *   onDownload={(id) => handleDownload(id)}
 *   onDelete={(id) => handleDelete(id)}
 *   showActions
 * />
 * ```
 */
export const FileCard = forwardRef<HTMLDivElement, FileCardProps>(
  (props, ref) => {
    const {
      file,
      onDownload,
      onDelete,
      onPreview,
      showActions = true,
      variant = 'detailed',
      gradientVariant = 'primary',
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const FileIcon = getFileIcon(file.mimeType);
    const iconColor = getFileMimeColor(file.mimeType);
    const fileTypeName = getFileTypeName(file.mimeType);

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDownload?.(file.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(file.id);
    };

    const handlePreview = (e: React.MouseEvent) => {
      e.stopPropagation();
      onPreview?.(file.id);
    };

    // Compact variant
    if (variant === 'compact') {
      return (
        <Card
          ref={ref}
          variant="elevated"
          hoverable
          hoverEffect="lift"
          onClick={onPreview ? () => onPreview(file.id) : undefined}
          style={{ cursor: onPreview ? 'pointer' : 'default' }}
        >
          <Group justify="space-between">
            <Group gap="sm">
              <Avatar
                size="md"
                radius="sm"
                style={{ backgroundColor: iconColor }}
              >
                <FileIcon size={20} color="white" />
              </Avatar>

              <Stack gap={2}>
                <Text size="sm" fw={500} truncate style={{ maxWidth: 200, ...accessibilityStyles }}>
                  {file.fileName}
                </Text>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatFileSize(file.fileSize)}
                </Text>
              </Stack>
            </Group>

            {showActions && (
              <Group gap="xs">
                {onPreview && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handlePreview}
                    aria-label="Preview file"
                  >
                    <IconEye size={16} />
                  </Button>
                )}
                {onDownload && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handleDownload}
                    aria-label="Download file"
                  >
                    <IconDownload size={16} />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="subtle"
                    gradientVariant="danger"
                    size="xs"
                    onClick={handleDelete}
                    aria-label="Delete file"
                  >
                    <IconTrash size={16} />
                  </Button>
                )}
              </Group>
            )}
          </Group>
        </Card>
      );
    }

    // Detailed variant
    return (
      <Card
        ref={ref}
        variant="elevated"
        hoverable
        hoverEffect="lift"
        onClick={onPreview ? () => onPreview(file.id) : undefined}
        style={{ cursor: onPreview ? 'pointer' : 'default' }}
      >
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              <Avatar
                size="lg"
                radius="md"
                style={{ backgroundColor: iconColor }}
              >
                <FileIcon size={28} color="white" />
              </Avatar>

              <Stack gap={4}>
                <Text size="md" fw={500} truncate style={{ maxWidth: 300, ...accessibilityStyles }}>
                  {file.fileName}
                </Text>
                <Group gap="xs">
                  <Badge size="sm" variant="light">
                    {fileTypeName}
                  </Badge>
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatFileSize(file.fileSize)}
                  </Text>
                </Group>
              </Stack>
            </Group>

            {file.status && (
              <Badge
                size="sm"
                variant="filled"
                gradientVariant={
                  file.status === 'completed'
                    ? 'success'
                    : file.status === 'failed'
                    ? 'danger'
                    : 'info'
                }
              >
                {file.status}
              </Badge>
            )}
          </Group>

          {(file.uploadedAt || file.uploadedBy) && (
            <Group gap="md">
              {file.uploadedAt && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Uploaded {formatTimeAgo(file.uploadedAt)}
                </Text>
              )}
              {file.uploadedBy && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  by {file.uploadedBy}
                </Text>
              )}
            </Group>
          )}

          {showActions && (
            <Group gap="xs" justify="flex-end">
              {onPreview && (
                <Button
                  variant="light"
                  gradientVariant={gradientVariant}
                  leftSection={<IconEye size={16} />}
                  onClick={handlePreview}
                >
                  Preview
                </Button>
              )}
              {onDownload && (
                <Button
                  variant="light"
                  gradientVariant={gradientVariant}
                  leftSection={<IconDownload size={16} />}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="light"
                  gradientVariant="danger"
                  leftSection={<IconTrash size={16} />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </Card>
    );
  }
);

FileCard.displayName = 'FileCard';

export default FileCard;
